import Decimal from 'decimal.js';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { getAssetCategoryDisplay } from '@/modules/assets/assetCategoryUtils';
import { AssetV2Fragment } from '@/modules/assets/graphql/Asset.generated';
import { AssetV2Extended } from '@/modules/assetValuation/AssetValuationTable/AssetValuationDisplayTable';
import { AccountCurrentAssetValuationFragment } from '@/modules/assetValuation/graphql/entityValuationQueries.generated';
import { AssetClassFragment } from '@/modules/tenant/graphql/TenantInformation.generated';
import { useEntitySyncWorkflowTracker } from '@/modules/workflowTrackers/hooks/useDoesEntityHaveActiveSync';
import { WorkflowTrackerStatus } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

const EMPTY_PREVIEW_ASSETS_RESULT = {
  assets: [],
  valuation: null,
};

/**
 * @description This function is responsible for returning a max of four assets from the current
 * valuation, sorted by highest value. Some integrated accounts have a very high number of assets,
 * and we don't have room to show them all, so we limit them to a max of NUMBER_PREVIEW_ASSETS,
 * but also return the totalCount of assets to indicate to the user whether or not
 * we're showing all of the assets.
 */
export function getSortedAssetsFromAccount(
  account: AccountCurrentAssetValuationFragment | null | undefined,
  assetClassesById: Record<string, AssetClassFragment>
) {
  if (!account) return EMPTY_PREVIEW_ASSETS_RESULT;

  // if we have a valid value, this will return 1 valuation. otherwise, it will return an empty array
  const mostRecentValuations = getNodes(account.valuations);
  const mostRecentValuation = mostRecentValuations[0];
  if (!mostRecentValuation) {
    return EMPTY_PREVIEW_ASSETS_RESULT;
  }

  return {
    assets: getSortedPreviewAssets(
      getNodes(mostRecentValuation.assets),
      assetClassesById
    ),
    valuation: mostRecentValuation,
  };
}

export function getSortedPreviewAssets(
  assets: AssetV2Fragment[],
  assetClassesById: Record<string, AssetClassFragment>
): AssetV2Extended[] {
  const previewAssets: AssetV2Extended[] = assets
    .map((asset) => getExtendedAssetFromFragment(asset, assetClassesById))
    .sort((a, b) => {
      if (
        (a.assetValue?.value ?? new Decimal(0)).greaterThan(
          b.assetValue?.value ?? new Decimal(0)
        )
      ) {
        return -1;
      }
      return 1;
    });

  return previewAssets;
}

export function getExtendedAssetFromFragment(
  asset: AssetV2Fragment,
  assetClassesById: Record<string, AssetClassFragment>
): AssetV2Extended {
  const displayCategory = getAssetCategoryDisplay(
    // TODO (T2-2751) make this required
    { categoryId: asset.class?.id },
    assetClassesById
  );
  return {
    ...asset,
    displayCategory,
  };
}

interface HandleEntitySyncWorkflowOptions {
  enabled?: boolean;
  onComplete: () => void;
}

export function useHandleEntitySyncWorkflow(
  entityId: string,
  { enabled = true, onComplete }: HandleEntitySyncWorkflowOptions
) {
  const tracker = useEntitySyncWorkflowTracker(entityId);
  const previousTrackerStatus = usePrevious(tracker?.status);

  useEffect(() => {
    const wasPending =
      previousTrackerStatus === WorkflowTrackerStatus.Queued ||
      previousTrackerStatus === WorkflowTrackerStatus.Started;
    const isComplete =
      tracker?.status === WorkflowTrackerStatus.Completed ||
      tracker?.status === WorkflowTrackerStatus.Failed;

    if (enabled && wasPending && isComplete) {
      onComplete();
    }
  }, [enabled, onComplete, previousTrackerStatus, tracker?.status]);

  return tracker?.status ?? null;
}
